import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Calendar as CalendarIcon, BookOpen, AlertCircle, TrendingUp, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyCollegeCourses() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [courses, setCourses] = useState([]);
  const [syllabusData, setSyllabusData] = useState({});
  const [loading, setLoading] = useState(true);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [statusRes, coursesRes] = await Promise.all([
        axios.get(`${API}/study-buddy/subscription-status`, { withCredentials: true }),
        axios.get(`${API}/study-buddy/courses`, { withCredentials: true })
      ]);
      
      setSubscriptionStatus(statusRes.data);
      
      // Check if user has subscription
      if (!statusRes.data.has_subscription) {
        toast.error('Premium subscription required to access My College Courses');
        navigate('/study-buddy/subscribe');
        return;
      }

      setCourses(coursesRes.data);

      // Load syllabus data for each course to get deadlines
      const syllabusPromises = coursesRes.data.map(course =>
        axios.get(`${API}/study-buddy/courses/${course.id}/syllabus`, { withCredentials: true })
          .then(res => ({ courseId: course.id, data: res.data }))
          .catch(() => ({ courseId: course.id, data: null }))
      );

      const syllabusResults = await Promise.all(syllabusPromises);
      
      const syllabusMap = {};
      const allDeadlines = [];

      syllabusResults.forEach(result => {
        if (result.data) {
          syllabusMap[result.courseId] = result.data;
          
          // Extract upcoming deadlines
          const course = coursesRes.data.find(c => c.id === result.courseId);
          if (result.data.items) {
            result.data.items.forEach(item => {
              if (item.due_date) {
                const dueDate = new Date(item.due_date);
                const today = new Date();
                const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                
                // Include items due in the next 30 days
                if (diffDays >= 0 && diffDays <= 30) {
                  allDeadlines.push({
                    ...item,
                    course_name: course.course_name,
                    course_id: course.id,
                    days_until: diffDays
                  });
                }
              }
            });
          }
        }
      });

      // Sort deadlines by due date
      allDeadlines.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      setUpcomingDeadlines(allDeadlines.slice(0, 10)); // Show top 10
      setSyllabusData(syllabusMap);

    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load courses data');
    } finally {
      setLoading(false);
    }
  };

  const getItemTypeColor = (type) => {
    const colors = {
      assignment: 'bg-blue-100 text-blue-800 border-blue-300',
      exam: 'bg-red-100 text-red-800 border-red-300',
      quiz: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      project: 'bg-purple-100 text-purple-800 border-purple-300',
      reading: 'bg-green-100 text-green-800 border-green-300',
      other: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[type] || colors.other;
  };

  const getUrgencyColor = (daysUntil) => {
    if (daysUntil === 0) return 'text-red-600 font-bold';
    if (daysUntil <= 3) return 'text-orange-600 font-semibold';
    if (daysUntil <= 7) return 'text-yellow-700';
    return 'text-gray-600';
  };

  const formatDaysUntil = (days) => {
    if (days === 0) return 'Due Today! 🔥';
    if (days === 1) return 'Due Tomorrow';
    if (days <= 7) return `Due in ${days} days`;
    return `Due in ${days} days`;
  };

  const getCourseSummary = (course) => {
    const syllabus = syllabusData[course.id];
    if (!syllabus || !syllabus.items) {
      return { total: 0, upcoming: 0 };
    }

    const total = syllabus.items.length;
    const upcoming = syllabus.items.filter(item => {
      if (!item.due_date) return false;
      const dueDate = new Date(item.due_date);
      const diffDays = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 30;
    }).length;

    return { total, upcoming };
  };

  if (!user) {
    return null;
  }

  return (
    <Layout>
      <section className="min-h-screen py-16 bg-gradient-to-b from-[#F4E4C1]/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/study-buddy')}
              className="text-[#2C1810] hover:text-[#E85D4D] font-semibold mb-4 inline-flex items-center"
            >
              ← Back to Study Buddy
            </button>
            <h1 className="text-4xl sm:text-5xl font-bold retro-title text-[#2C1810] mb-4">
              My College Courses
            </h1>
            <p className="text-xl text-gray-700">
              Your centralized dashboard for all college courses and deadlines
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E8934D]"></div>
            </div>
          ) : (
            <>
              {/* Quick Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                <div className="cassette-card p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-[#E8934D]/20 rounded-lg">
                      <BookOpen className="w-8 h-8 text-[#E8934D]" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-[#2C1810]">{courses.length}</div>
                      <div className="text-sm text-gray-600">Total Courses</div>
                    </div>
                  </div>
                </div>

                <div className="cassette-card p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <AlertCircle className="w-8 h-8 text-orange-600" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-[#2C1810]">{upcomingDeadlines.length}</div>
                      <div className="text-sm text-gray-600">Upcoming Deadlines</div>
                    </div>
                  </div>
                </div>

                <div className="cassette-card p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <TrendingUp className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-[#2C1810]">
                        {Object.values(syllabusData).reduce((sum, s) => sum + (s?.items?.length || 0), 0)}
                      </div>
                      <div className="text-sm text-gray-600">Total Assignments</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Calendar Button */}
              {courses.length > 0 && (
                <div className="cassette-card p-6 mb-8 text-center">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <CalendarIcon className="w-8 h-8 text-[#E8934D]" />
                    <h2 className="text-2xl font-bold text-[#2C1810]">Unified Calendar</h2>
                  </div>
                  <p className="text-gray-700 mb-6">View all your course events and deadlines in one interactive calendar</p>
                  <button
                    onClick={() => navigate('/study-buddy/calendar')}
                    className="px-8 py-4 bg-[#E8934D] text-white font-bold text-lg rounded-lg hover:bg-[#E85D4D] transition-colors shadow-lg"
                  >
                    📅 Open All Courses Calendar
                  </button>
                </div>
              )}

              {/* Upcoming Deadlines Section */}
              {upcomingDeadlines.length > 0 && (
                <div className="cassette-card p-6 mb-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Clock className="w-6 h-6 text-[#E8934D]" />
                    <h2 className="text-2xl font-bold text-[#2C1810]">Upcoming Deadlines</h2>
                  </div>
                  
                  <div className="space-y-3">
                    {upcomingDeadlines.map((deadline, index) => (
                      <div
                        key={index}
                        onClick={() => navigate(`/study-buddy/course/${deadline.course_id}`)}
                        className="p-4 bg-gradient-to-r from-[#F4E4C1]/50 to-white rounded-lg border border-[#E8B67A] hover:shadow-lg transition-all cursor-pointer"
                      >
                        <div className="flex items-start justify-between flex-wrap gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-xs px-2 py-1 rounded border ${getItemTypeColor(deadline.item_type)}`}>
                                {deadline.item_type || 'other'}
                              </span>
                              <span className="text-sm font-semibold text-gray-600">
                                {deadline.course_name}
                              </span>
                            </div>
                            <h3 className="text-lg font-bold text-[#2C1810] mb-1">
                              {deadline.title}
                            </h3>
                            {deadline.description && (
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {deadline.description}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className={`text-sm font-semibold ${getUrgencyColor(deadline.days_until)}`}>
                              {formatDaysUntil(deadline.days_until)}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {new Date(deadline.due_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Courses Section */}
              <div className="cassette-card p-6">
                <div className="flex items-center gap-3 mb-6">
                  <BookOpen className="w-6 h-6 text-[#E8934D]" />
                  <h2 className="text-2xl font-bold text-[#2C1810]">All Courses</h2>
                </div>

                {courses.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600 mb-4">No courses yet. Start by creating your first course!</p>
                    <button
                      onClick={() => navigate('/study-buddy')}
                      className="px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors"
                    >
                      Create Course
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {courses.map(course => {
                      const summary = getCourseSummary(course);
                      return (
                        <div
                          key={course.id}
                          onClick={() => navigate(`/study-buddy/course/${course.id}`)}
                          className="p-5 bg-gradient-to-br from-[#F4E4C1]/40 to-white rounded-lg border-2 border-[#E8B67A] hover:shadow-xl transition-all cursor-pointer group"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-xl font-bold text-[#2C1810] mb-1 group-hover:text-[#E85D4D] transition-colors line-clamp-2">
                                {course.course_name}
                              </h3>
                              {course.course_code && (
                                <span className="text-sm text-gray-600 font-semibold">
                                  {course.course_code}
                                </span>
                              )}
                            </div>
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
                          </div>

                          <div className="mb-3">
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold">{course.school_name}</span>
                            </p>
                            {course.instructor_name && (
                              <p className="text-xs text-gray-600">
                                Instructor: {course.instructor_name}
                              </p>
                            )}
                            {course.semester && (
                              <p className="text-xs text-gray-600">
                                {course.semester}
                              </p>
                            )}
                          </div>

                          <div className="pt-3 border-t border-[#E8B67A]/50">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">
                                📚 {summary.total} items
                              </span>
                              {summary.upcoming > 0 && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-semibold">
                                  {summary.upcoming} upcoming
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
